import { useMemo, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';

import DataTable from '@/components/data-table/data-table';
import { formatCurrency, formatDate } from '@/utils/format';

import { ORDER_STATUS_LABELS, ORDER_STATUS_GROUPS } from '@/features/orders/orders.types';
import type { Order, OrderSearchField } from '@/features/orders/orders.types';
import type { ColumnDef, CellContext } from '@tanstack/react-table';

import { ChangeStatusModal } from './components/change-status-modal';
import { OrderDrawer } from './order-drawer';
import { useOrdersPage } from './use-orders-page';

const STATUS_BADGE_CLASS: Record<number, string> = {
  1: 'badge-ghost',
  2: 'badge-info',
  3: 'badge-warning',
  4: 'badge-success',
  5: 'badge-primary',
  6: 'badge-success',
  8: 'badge-error',
  10: 'badge-error',
  11: 'badge-warning',
  12: 'badge-warning',
  13: 'badge-success',
};

const METRIC_LABELS: Record<string, string> = {
  por_recibir: 'Por recibir',
  en_punto: 'En Punto99',
  recolectadas: 'Recolectadas',
  entregadas: 'Entregadas',
  por_devolver: 'Por devolver',
  canceladas: 'Canceladas',
};

export function OrdersPage() {
  const {
    items,
    total,
    page,
    metrics,
    isLoading,
    isFetching,
    isError,
    search,
    search_field,
    sort_by,
    sort_order,
    limit,
    selected_order,
    is_change_status_modal_open,
    orders_to_update,
    onSearchChange,
    onSearchFieldChange,
    onSort,
    onPageChange,
    onLimitChange,
    onSelectOrder,
    onOpenChangeStatusModal,
    onCloseChangeStatusModal,
    onToggleShowDeleted,
    show_deleted,
  } = useOrdersPage();

  const groupedMetrics = useMemo(() => {
    if (!metrics) return null;
    const groups: Record<string, number> = {
      por_recibir: 0,
      en_punto: 0,
      recolectadas: 0,
      entregadas: 0,
      por_devolver: 0,
      canceladas: 0,
    };

    metrics.by_status.forEach((item) => {
      Object.entries(ORDER_STATUS_GROUPS).forEach(([groupName, ids]) => {
        if ((ids as readonly number[]).includes(item.status_id)) {
          groups[groupName] += item.count;
        }
      });
    });

    return groups;
  }, [metrics]);

  const columns = useMemo<ColumnDef<Order>[]>(
    () => [
      {
        accessorKey: 'tracking_number',
        header: (): ReactNode => (
          <button className="flex items-center gap-1" onClick={() => onSort('status_id')}>
            Guía
            {sort_by === 'status_id' ? (
              sort_order === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
            ) : (
              <ArrowUpDown size={14} className="opacity-20" />
            )}
          </button>
        ),
        cell: (info: CellContext<Order, string | null>) => {
          const order = info.row.original;
          const display_id = order.tracking_number || order.external_reference || order.id;
          return (
            <button
              type="button"
              className="font-semibold font-mono hover:text-primary transition-colors text-left"
              title={order.id}
              onClick={() => onSelectOrder(order)}
            >
              {display_id}
            </button>
          );
        },
      },
      {
        accessorKey: 'customer_name',
        header: 'Cliente',
        cell: (info: CellContext<Order, string | null>) => (info.getValue() as string) || '—',
      },
      {
        accessorKey: 'dispatch_place_name',
        header: 'Punto de despacho',
      },
      {
        accessorKey: 'country_code',
        header: 'País',
        size: 80,
        cell: (info: CellContext<Order, string>) => (info.getValue() as string)?.toUpperCase(),
      },
      {
        accessorKey: 'status_id',
        header: 'Estado',
        size: 160,
        cell: (info: CellContext<Order, number>) => {
          const order = info.row.original;
          const statusId = info.getValue() as number;

          if (order.deleted) {
            return (
              <div className="flex flex-col gap-1 items-start">
                <span className="badge badge-sm badge-error font-bold" title="Eliminada">Eliminada</span>
                {order.deleted_at && (
                  <span className="text-[10px] opacity-50 font-mono">
                    {formatDate(order.deleted_at)}
                  </span>
                )}
              </div>
            );
          }

          const statusText = ORDER_STATUS_LABELS[statusId] || order.status;

          return (
            <div className="w-full overflow-hidden">
              <span
                className={twMerge(
                  'badge badge-sm font-medium max-w-full overflow-hidden',
                  STATUS_BADGE_CLASS[statusId] || 'badge-ghost',
                )}
                title={statusText}
              >
                <span className="min-w-0 truncate">{statusText}</span>
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'package_count',
        header: 'Paquetes',
        size: 100,
        meta: { align: 'center' },
      },
      {
        id: 'audit_reason',
        header: 'Motivo / Responsable',
        size: 200,
        cell: (info: CellContext<Order, unknown>) => {
          const order = info.row.original;
          if (!order.deleted) return '—';

          const last_change = order.manual_changes?.[order.manual_changes.length - 1];
          const reason = order.deleted_reason || last_change?.reason || 'Sin motivo';

          let responsible = '—';
          if (last_change?.responsible) {
            responsible = last_change.responsible.name || last_change.responsible.email || '—';
          }

          return (
            <div className="flex flex-col gap-0.5 leading-tight">
              <span className="text-xs font-medium line-clamp-1 italic">
                "{reason}"
              </span>
              <span className="text-[10px] opacity-60">
                Por: {responsible}
              </span>
            </div>
          );
        },
      },
      {
        id: 'amount',
        header: 'Monto',
        size: 120,
        meta: { align: 'right' },
        cell: (info: CellContext<Order, string>) =>
          formatCurrency(parseFloat(info.row.original.amount || '0'), info.row.original.currency),
      },
      {
        accessorKey: 'created_at',
        header: (): ReactNode => (
          <button className="flex items-center gap-1" onClick={() => onSort('created_at')}>
            Creada
            {sort_by === 'created_at' ? (
              sort_order === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
            ) : (
              <ArrowUpDown size={14} className="opacity-20" />
            )}
          </button>
        ),
        size: 120,
        cell: (info: CellContext<Order, string>) => formatDate(info.getValue() as string),
      },
      {
        id: 'actions',
        size: 48,
        meta: { align: 'center' },
        header: '',
        cell: (info: CellContext<Order, unknown>) => {
          const is_deleted = info.row.original.deleted;
          if (is_deleted) return null;

          const total_rows = info.table.getRowModel().rows.length;
          const is_near_bottom = info.row.index >= total_rows - 3;

          return (
            <div className={twMerge('dropdown dropdown-end', is_near_bottom && 'dropdown-top')}>
              <button
                tabIndex={0}
                type="button"
                className="btn btn-ghost btn-xs btn-square"
                aria-label="Opciones"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              <ul
                tabIndex={0}
                className="dropdown-content z-[50] menu menu-sm bg-base-100 border border-base-300 rounded-box shadow-lg w-44 p-1"
              >
                <li>
                  <button type="button" onClick={() => onSelectOrder(info.row.original)}>
                    Detalle de la orden
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="text-error"
                    onClick={() => onOpenChangeStatusModal([info.row.original])}
                  >
                    <Trash2 className="h-4 w-4" />
                    Eliminar orden
                  </button>
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [onSort, sort_by, sort_order, onSelectOrder, onOpenChangeStatusModal],
  );

  if (isError) {
    return (
      <div className="flex justify-center p-8">
        <div className="alert alert-error max-w-lg">
          <span>No se pudieron cargar las órdenes. Intenta de nuevo.</span>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-4">
        <h1 className="text-3xl font-bold">Órdenes</h1>

        {/* Metrics Header */}
        {!show_deleted && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {groupedMetrics &&
              Object.entries(groupedMetrics).map(([key, value]) => (
                <div
                  key={key}
                  className="card bg-base-100 border border-base-300 p-4 flex flex-col items-center"
                >
                  <span className="text-xs text-base-content/60 uppercase font-bold tracking-wider">
                    {METRIC_LABELS[key]}
                  </span>
                  <span className="text-2xl font-black">{value}</span>
                </div>
              ))}
          </div>
        )}
      </header>

      <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex flex-1 w-full gap-2 items-center">
          {/* Active / Inactive Toggle */}
          <div className="join border border-base-300">
            <button
              className={twMerge('join-item btn btn-sm', !show_deleted ? 'btn-primary' : 'btn-ghost')}
              onClick={() => show_deleted && onToggleShowDeleted()}
            >
              Activas
            </button>
            <button
              className={twMerge('join-item btn btn-sm', show_deleted ? 'btn-primary' : 'btn-ghost')}
              onClick={() => !show_deleted && onToggleShowDeleted()}
            >
              Inactivas
            </button>
          </div>

          <div className="divider divider-horizontal mx-0" />

          {/* Search Bar + Field Selector */}
          <div className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar..."
                className="input input-bordered w-full pl-10 h-10"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            <select
              className="select select-bordered h-10 min-h-0"
              value={search_field}
              onChange={(e) => onSearchFieldChange(e.target.value as OrderSearchField)}
            >
              <option value="todos">Todos los campos</option>
              <option value="guia">Guía / Tracking</option>
              <option value="cliente">Cliente</option>
              <option value="punto">Punto de despacho</option>
              <option value="referencia">Referencia externa</option>
            </select>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs opacity-60">Filas:</span>
            <select
              className="select select-bordered select-xs"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className="join">
            <button
              className="join-item btn btn-xs"
              disabled={page <= 1 || isLoading}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft size={16} />
            </button>
            <button className="join-item btn btn-xs no-animation cursor-default">
              Pág {page} de {totalPages || 1}
            </button>
            <button
              className="join-item btn btn-xs"
              disabled={page >= totalPages || isLoading}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <DataTable columns={columns} data={items} isLoading={isLoading} isFetching={isFetching} />

      <div className="text-xs text-base-content/40 text-center">
        Mostrando {items.length} de {total} órdenes registradas.
      </div>

      <OrderDrawer order={selected_order} onClose={() => onSelectOrder(null)} />

      <ChangeStatusModal
        open={is_change_status_modal_open}
        orders={orders_to_update}
        onClose={onCloseChangeStatusModal}
      />
    </div>
  );
}
